/**
 * @file components/DataTable.jsx
 * @description Reusable, accessible data table with loading, empty, and error states.
 *
 * @param {Object}    props
 * @param {Array}     props.columns       - Column definitions.
 * @param {string}    props.columns[].key - Data key to read from each row object.
 * @param {string}    props.columns[].label - Column header label.
 * @param {Function}  [props.columns[].render] - Optional custom cell renderer (row) => ReactNode.
 * @param {Array}     props.data          - Array of row objects.
 * @param {boolean}   [props.isLoading]   - Shows a spinner overlay when true.
 * @param {string}    [props.error]       - Error message to display instead of the table.
 * @param {string}    [props.emptyMessage] - Message shown when data is empty.
 * @param {Function}  [props.getRowKey]   - Returns a unique key for each row (defaults to row.id).
 */

import React from 'react';

/**
 * Renders a Bootstrap-styled data table with built-in loading, empty, and error states.
 */
export default function DataTable({
  columns,
  data = [],
  isLoading = false,
  error,
  emptyMessage = 'No records found.',
  getRowKey = (row) => row.id,
}) {
  // ── Error state ───────────────────────────────────────────────────────────
  if (error) {
    return (
      <div className="alert alert-danger d-flex align-items-center gap-2" role="alert">
        <i className="bi bi-exclamation-triangle-fill" aria-hidden="true" />
        <span>{error}</span>
      </div>
    );
  }

  return (
    <div className="position-relative">
      {/* Loading overlay — table structure stays mounted for layout stability */}
      {isLoading && (
        <div
          className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
          style={{ background: 'rgba(255,255,255,0.7)', zIndex: 10, minHeight: 80 }}
          aria-live="polite"
          aria-label="Loading data"
        >
          <div
            className="spinner-border"
            style={{ color: 'var(--color-primary)' }}
            role="status"
          >
            <span className="visually-hidden">Loading…</span>
          </div>
        </div>
      )}

      {/* ── Table ─────────────────────────────────────────────────────────── */}
      <div className="table-responsive">
        <table className="table table-hover table-bordered align-middle mb-0">
          <thead style={{ backgroundColor: 'var(--color-primary)', color: 'var(--color-text-light)' }}>
            <tr>
              {columns.map((col) => (
                <th key={col.key} scope="col">
                  {col.label}
                </th>
              ))}
            </tr>
          </thead>

          <tbody>
            {data.length === 0 && !isLoading ? (
              <tr>
                <td
                  colSpan={columns.length}
                  className="text-center text-muted py-4"
                >
                  <i className="bi bi-inbox fs-4 d-block mb-1" aria-hidden="true" />
                  {emptyMessage}
                </td>
              </tr>
            ) : (
              data.map((row) => (
                <tr key={getRowKey(row)}>
                  {columns.map((col) => (
                    <td key={col.key}>
                      {col.render ? col.render(row) : row[col.key]}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
